import React from 'react';

import i18n from 'I18n';
import config from 'config';

import { ReactHookFormMarkDown } from 'components/base/CustomMarkDownArea';
import { errorMessage, hasError } from 'components/base/ErrorFieldMessage';
import InputFieldControl from 'components/base/InputFieldControl';
import TypeSelectArea from 'components/base/TypeSelectArea';
import PeriodAndWeekdaySection from 'components/base/period/PeriodAndWeekdaySection';
import PeriodSection from 'components/base/period/PeriodSection';
import ApprovalCheckList from 'components/campaign/campaignCreation/ApprovalCheckList';
import BrandDropdownWithButtonAndPrompt from 'components/campaign/campaignCreation/BrandDropdownWithButtonAndPrompt';
import { getTipsImages } from 'components/campaign/campaignCreation/CampaignStepTwoPreviewConfig';
import CategorySelectorWithPrompt from 'components/campaign/campaignCreation/CategorySelectorWithPrompt';
import ContentLanguageCard from 'components/campaign/campaignCreation/ContentLanguageCard';
import { CampaignTypesConfig, getCampaignTypeSelectPromptConfig } from 'components/campaign/campaignCreation/CreateCampaignStepOneConfigs';
import { campaignAvailableClue, campaignLimitDate, disableSwitchToAllPeriod } from 'components/campaign/campaignCreation/CreateCampaignUtil';
import EarningRuleDropdownWithButtonAndPrompt from 'components/campaign/campaignCreation/EarningRuleDropdownWithButtonAndPrompt';
import FeaturedOrExclusiveCampaignRadio from 'components/campaign/campaignCreation/FeaturedOrExclusiveCampaignRadio';
import LinkedCouponTimeSlotSection from 'components/campaign/campaignCreation/LinkedCouponTimeSlotSection';
import OtherInfomationiInput from 'components/campaign/campaignCreation/OtherInfomationiInput';
import PhotoUploaderWithSwitch from 'components/campaign/campaignCreation/PhotoUploaderWithSwitch';
import PrefillSettingBySearch from 'components/campaign/campaignCreation/PrefillSettingBySearch';
import ProfileSection from 'components/campaign/campaignCreation/ProfileSection';
import ReactFormTargetCustomerSelector from 'components/campaign/campaignCreation/ReactFormTargetCustomerSelector';
import VisibleMallSection from 'components/campaign/campaignCreation/VisibleMallSection';
import VisiblePeriodSection from 'components/campaign/campaignCreation/VisiblePeriodSection';
import CouponDropdownWithButtonAndPrompt from 'components/earning/CouponDropdownWithButtonAndPrompt';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';
import ReactHookFormMarkDownArea from 'components/reactHookFormComponents/ReactHookFormMarkDownArea';
import ReactHookFormMultipleSelector from 'components/reactHookFormComponents/ReactHookFormMultipleSelector';
import ReactHookFormSwitch from 'components/reactHookFormComponents/ReactHookFormSwitch';
import ReactHookFormTextInput from 'components/reactHookFormComponents/ReactHookFormTextInput';
import { CampaignType, EarningRuleType, LanguageConfig, showCampaignPublishChannelSelector } from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import { ProcessEnvConfig } from 'config/ProcessEnvConfig';
import { publishChannelList } from 'models/CreateCampaignModel';
import RecurringCampaignSwitch from 'components/campaign/campaignCreation/RecurringCampaignSwitch'
import RecurringCampaignPeriodSelector from 'components/campaign/campaignCreation/RecurringCampaignPeriodSelector'
import RecurringMessages from 'components/campaign/campaignCreation/RecurringMessages'

import { createAction } from 'utils';
import { compareDate } from 'utils/TimeFormatUtil';

const fetchLinkedDataAndSetCampaignName = ({
  dispatch,
  setValue,
  currentTranslations,
  actionName,
  idKey,
  idValue,
  dataKey,
}) => {
  dispatch(
    createAction(actionName)({
      [idKey]: idValue,
      afterAction: (data) => {
        const sourceTranslations = { ...currentTranslations };
        (Object.keys(data[dataKey]?.translations) || []).forEach((lang) => {
          const translation = data[dataKey]?.translations[lang];
          (Object.keys(translation) || []).forEach((key) => {
            if (sourceTranslations[lang] && key in sourceTranslations[lang]) {
              setValue(`translations.${lang}.${key}`, translation[key], {
                shouldDirty: true,
              });
            } else if (
              !sourceTranslations[lang] &&
              ['id', 'pk'].indexOf(key) < 0
            ) {
              setValue(`translations.${lang}.${key}`, translation[key], {
                shouldDirty: true,
              });
            }
          });
        });
      },
    }),
  );
};
export const CREATION_STEP_ONE_CONFIG = {
  extraValidate: ({ getValues, setError, clearErrors }) => true,
  getDisplayComponentKeys: ({ formContext: { watch } }) => {
    const campaignType = watch('campaignType');
    const displayNormalSectionComponents = [
      'prefill_setting_by_url_search_key',
      'type_selector_key',
    ];
    const displayLanguageSectionComponents = [];
    if (campaignType === CampaignType.generalMessageCampaign) {
      return {
        displayNormalSectionComponents,
        displayLanguageSectionComponents,
      };
    }
    if (campaignType === CampaignType.couponsCampaign) {
      displayNormalSectionComponents.push('coupon_set_dropdown_key');
    } else if (campaignType === CampaignType.earningRulesCampaign) {
      displayNormalSectionComponents.push('earning_rule_dropdown_key');
    }
    if (showCampaignPublishChannelSelector) {
      displayNormalSectionComponents.push('publish_channel_selector_key');
    }

    return {
      displayNormalSectionComponents,
      displayLanguageSectionComponents,
    };
  },
  normalSections: [
    {
      sectionTitle: '',
      sectionContent: [
        {
          key: 'prefill_setting_by_url_search_key',
          component: PrefillSettingBySearch,
        },
        {
          key: 'type_selector_key',
          complexComponent: ({ formContext: { clearErrors, setValue } }) => (
            <TypeSelectArea
              title="Campaign Type"
              typeConfig={CampaignTypesConfig}
              typeFieldName="campaignType"
              extraValueChangeAction={({ dispatch, value }) => {
                clearErrors();
                dispatch(
                  createAction('createCampaign/typeChange')({
                    campaignType: value,
                  }),
                );
                setValue('linkedEarningRules', {}, { shouldDirty: true });
                setValue('linkedCoupon', {}, { shouldDirty: true });
              }}
            />
          ),
        },
        {
          key: 'coupon_set_dropdown_key',
          complexComponent: ({
            dispatch,
            formContext: { setValue, watch },
          }) => {
            return (
              <CouponDropdownWithButtonAndPrompt
                title="Link to coupon set"
                valueFieldName="linkedCoupon"
                description="Please link to an existing coupon set. After you linked to an existing coupon set, the system will auto-fill in the content of the coupon set in the next step."
                hasAddButton={true}
                addButtonInfo={{
                  requires: PermissionCodes.changeCouponTemplate,
                }}
                promptConfig={getCampaignTypeSelectPromptConfig(
                  CampaignType.couponsCampaign,
                )}
                customOnChange={(coupon) => {
                  fetchLinkedDataAndSetCampaignName({
                    dispatch,
                    setValue,
                    currentTranslations: watch('translations'),
                    actionName: 'createCampaign/getAndLinkCouponDetail',
                    idKey: 'couponId',
                    idValue: coupon.pk,
                    dataKey: 'linkedCoupon',
                  });
                }}
              />
            );
          },
        },
        {
          key: 'earning_rule_dropdown_key',
          complexComponent: ({
            dispatch,
            formContext: { setValue, watch },
          }) => {
            return (
              <EarningRuleDropdownWithButtonAndPrompt
                title="Link to earning rule set"
                valueFieldName="linkedEarningRules"
                description="Please link to an existing earning rule set. After you linked to an existing earning rule set, the system will auto-fill in the content of the earning rule set in the next step."
                hasAddButton={true}
                promptConfig={getCampaignTypeSelectPromptConfig(
                  CampaignType.earningRulesCampaign,
                )}
                customOnChange={(earningRule) => {
                  fetchLinkedDataAndSetCampaignName({
                    dispatch,
                    setValue,
                    currentTranslations: watch('translations'),
                    actionName: 'createCampaign/getAndLinkEarningRuleDetail',
                    idKey: 'earningRuleId',
                    idValue: earningRule.pk,
                    dataKey: 'linkedEarningRules',
                  });
                }}
                defaultFilter={{
                  reverse: true,
                  linkedCampaignType: CampaignType.earningRulesCampaign,
                  otherSearch: {
                    typeIn: [
                      EarningRuleType.qrCodeScanning,
                      EarningRuleType.gpsCheckIn,
                      EarningRuleType.memberReferral,
                    ],
                  },
                }}
              />
            );
          },
        },
      ],
    },
    {
      sectionTitle: 'publish channel',
      description: 'select the channel to be published.',
      sectionContent: [
        {
          key: 'publish_channel_selector_key',
          component: ReactHookFormMultipleSelector,
          componentProps: {
            valueFieldName: 'publishChannel',
            sourceData: publishChannelList,
            custom: {
              customTitle: <CustomTitleLabel title="Select publish channel" />,
            },
            hideTopSpace: true,
            size: ['shorter'],
          },
        },
      ],
    },
  ],
  languageSections: [],
};

export const campaignCreationStepTwoGeneralSectionConfig = {
  keys: [
    'general_name_key',
    'short_description_optional_key',
    'related_brand_optional_key',
  ],
  section: {
    sectionTitle: 'general',
    sectionContent: [
      {
        key: 'general_name_key',
        component: ReactHookFormTextInput,
        componentProps: {
          valueFieldName: 'generalName',
          withTranslation: true,
          forcusKey: 'name',
          title: 'name',
        },
      },
      {
        key: 'short_description_optional_key',
        complexComponent: ({ language, setActiveSection }) => (
          <ReactHookFormMarkDown
            content={{
              label: i18n.t('short_description_optional', { locale: language }),
              key: `translations.${language}.shortDescription`,
              focus: () => {
                setActiveSection('shortDescription');
              },
            }}
          />
        ),
      },
      {
        key: 'related_brand_optional_key',
        complexComponent: ({
          language,
          setActiveSection,
          formContext: { watch },
        }) => {
          if (language === LanguageConfig.english) {
            return (
              <BrandDropdownWithButtonAndPrompt
                labelContainPk={false}
                title={i18n.t('related_brand_optional', { locale: language })}
                defaultFilter={{ isSelectorLoad: true }}
                valueFieldName="linkedBrand"
                hasAddButton={true}
                isPrompt={true}
                promptConfig={{
                  title: 'Go to create brand?',
                  message: `You will leave the campaign creation process. After you create a new brand, you can back to this page.`,
                  button: 'Create Brand',
                  refreshButton: 'Done and refresh',
                  action: () => {
                    window.open('/brands/create', '_blank');
                  },
                }}
                onFocus={() => setActiveSection('relatedBrand')}
              />
            );
          }
          return (
            <div className="brand-select-dropdown-container">
              <CustomTitleLabel
                title={i18n.t('related_brand_optional', { locale: language })}
              />
              <label>{watch('linkedBrand')?.name || '-'}</label>
            </div>
          );
        },
      },
    ],
  },
};

export const CREATION_STEP_TWO_CONFIG = {
  extraValidate: ({ getValues, setError, clearErrors }) => true,
  getDisplayComponentKeys: ({ formContext: { watch } }) => {
    const isExclusive = watch('isExclusive');
    let displayLanguageSectionComponents =
      campaignCreationStepTwoGeneralSectionConfig.keys;
    displayLanguageSectionComponents = [
      ...displayLanguageSectionComponents,
      'cover_photo_upload_key',
      'featured_or_exclusive_campaign_key',
      ...(isExclusive ? ['square_cover_photo_upload_key'] : []),
      'other_photo_upload_key',
      'instruction_section_key',
      'detail_section_key',
    ];
    if (isExclusive) {
      displayLanguageSectionComponents.push('square_cover_photo_upload_key');
    }

    return {
      displayNormalSectionComponents: [],
      displayLanguageSectionComponents,
    };
  },
  normalSections: [],
  languageSections: [
    campaignCreationStepTwoGeneralSectionConfig.section,
    {
      sectionTitle: 'key_visual',
      description: 'campaign.cover_photo_reminder',
      descriptionClass: 'tips-message',
      sectionContent: [
        {
          key: 'cover_photo_upload_key',
          component: PhotoUploaderWithSwitch,
          componentProps: {
            withTranslation: true,
            title: 'cover_photo',
            tips: 'cover_photo_suggest_with_variable',
            wordTranslationVariable: config.campaignRectangleSize,
            tranlationFieldPrefix: 'translations',
            photoFieldName: 'coverPhoto',
            activeSectionKey: 'coverPhoto',
            switchConfig: {
              fieldName: 'applyCoverPhotoToAllLanguage',
              title: 'Apply to all language',
            },
            uploaderAspect: 900 / config.campaignRectangleSize.minHeight,
            minWidth: 900,
            minHeight: config.campaignRectangleSize.minHeight,
          },
        },
        {
          key: 'featured_or_exclusive_campaign_key',
          component: FeaturedOrExclusiveCampaignRadio,
        },
        {
          key: 'square_cover_photo_upload_key',
          component: PhotoUploaderWithSwitch,
          componentProps: {
            withTranslation: true,
            title: 'campaign.square_cover_photo',
            tips: 'square_cover_photo_suggest_with_variable',
            wordTranslationVariable: config.campaignSquareSize,
            tranlationFieldPrefix: 'translations',
            photoFieldName: 'squareCoverPhoto',
            activeSectionKey: 'squareCoverPhoto',
            switchConfig: {
              fieldName: 'applySquareCoverPhotoToAllLanguage',
              title: 'Apply to all language',
            },
            uploaderAspect:
              config.campaignSquareSize.minWidth /
              config.campaignSquareSize.minHeight,
          },
        },
        {
          key: 'other_photo_upload_key',
          component: PhotoUploaderWithSwitch,
          componentProps: {
            withTranslation: true,
            title: 'other_photos',
            tips: 'cover_photo_suggest_with_variable',
            wordTranslationVariable: config.campaignRectangleSize,
            tranlationFieldPrefix: 'translations',
            photoFieldName: 'detailPhotos',
            activeSectionKey: 'otherPhotos',
            switchConfig: {
              fieldName: 'applyDetailPhotosToAllLanguage',
              title: 'Apply to all language',
            },
            uploaderAspect: 900 / config.campaignRectangleSize.minHeight,
            minWidth: 900,
            minHeight: config.campaignRectangleSize.minHeight,
            maxImageNum: 4,
          },
        },
      ],
    },
    {
      sectionContent: [
        {
          key: 'instruction_section_key',
          complexComponent: ({
            setActiveSection,
            language,
            formContext: { watch },
          }) => {
            const tipsImageConfig = getTipsImages(watch('campaignType'));
            return (
              <ReactHookFormMarkDownArea
                hasTranslation
                areaTitle="instruction"
                focusChange={setActiveSection}
                language={language}
                titleConfig={{
                  label: 'instruction_section_title',
                  valueFieldName:
                    'translations.{{language}}.instructionSectionTitle',
                  disableFocusChange: false,
                  focusChangeKey: 'instructionTitle',
                  tips: tipsImageConfig.instructionTitle,
                }}
                contentConfig={{
                  label: 'instruction_section_content',
                  valueFieldName:
                    'translations.{{language}}.instructionSectionContent',
                  disableFocusChange: false,
                  focusChangeKey: 'instructionContent',
                  tips: tipsImageConfig.instructionContent,
                }}
              />
            );
          },
        },
      ],
    },
    {
      sectionContent: [
        {
          key: 'detail_section_key',
          complexComponent: ({
            setActiveSection,
            language,
            formContext: { watch },
          }) => {
            const tipsImageConfig = getTipsImages(watch('campaignType'));
            return (
              <ReactHookFormMarkDownArea
                focusChange={setActiveSection}
                language={language}
                areaTitle="detail"
                titleConfig={{
                  label: 'detail_section_title',
                  valueFieldName:
                    'translations.{{language}}.detailSectionTitle',
                  disableFocusChange: false,
                  focusChangeKey: 'detailsTitle',
                  tips: tipsImageConfig.detailsTitle,
                }}
                contentConfig={{
                  label: 'detail_section_content',
                  valueFieldName:
                    'translations.{{language}}.detailSectionContent',
                  disableFocusChange: false,
                  focusChangeKey: 'detailsContent',
                  tips: tipsImageConfig.detailsContent,
                }}
                hasTranslation
              />
            );
          },
        },
      ],
    },
  ],
};

export const CREATION_STEP_THREE_CONFIG = {
  // for Campaign Creation and Stamp Campaign Creation
  extraValidate: ({ getValues, setError, clearErrors }) => true,
  getDisplayComponentKeys: ({ formContext: { watch } }) => {
    const isExclusive = watch('isExclusive');
    const campaignType = watch('campaignType');
    const isBirthdayRecurringCampaign = watch('isBirthdayRecurringCampaign');
    const excludeBlacklistSegment = watch("excludeBlacklistSegment");
    let displayLanguageSectionComponents = [];
    let displayNormalSectionComponents = [
      ...(isExclusive ? ['target_customer_selector_key'] : []),
      'exclude_blacklist_segment_switch_key',
      'campaign_category_selector_key',
      ...(ProcessEnvConfig.haveShopMall ? ['visible_mall_key'] : []),
      'recurring_birthday_selector_key',
      ...(isBirthdayRecurringCampaign
        ? [
          'recurring_birthday_active_period_selector_key',
          'recurring_birthday_visible_period_selector_key',
          'recurring_message_selector_key'
        ]
        : []
      ),
      'active_period_key',
      'visible_period_section_key',
    ];
    if (excludeBlacklistSegment) {
      displayNormalSectionComponents.push("blacklist_segment_selector_key")
    }
    if (campaignType === CampaignType.ticketCampaign) {
      displayNormalSectionComponents = [
        ...displayNormalSectionComponents,
        'ticket_campaign_required_points_key',
        'enable_otp_key',
        'set_linked_coupon_time_slot_key',
        'fill_other_info_key',
        'other_info_input_section_key',
        'coupon_acquiring_limit_key',
        'per_head_limit_key',
      ];
    } else if (campaignType === CampaignType.couponsCampaign) {
      displayNormalSectionComponents = [
        ...displayNormalSectionComponents,
        'required_points_input_key',
        'coupon_acquiring_limit_key',
        'per_head_limit_key',
      ];
    }
    if (campaignType !== CampaignType.generalMessageCampaign) {
      displayNormalSectionComponents.push('blackout_period_key');
    }

    return {
      displayNormalSectionComponents,
      displayLanguageSectionComponents,
    };
  },
  languageSections: [],
  normalSections: [
    {
      getDisplaySectionTitle: ({ formContext: { watch } }) => {
        if (watch('campaignType') === CampaignType.couponsCampaign) {
          return 'General';
        }
        return null;
      },
      sectionTitle: '',
      sectionContent: [
        {
          key: 'target_customer_selector_key',
          complexComponent: ({ formContext: { watch } }) => (
            <ReactFormTargetCustomerSelector
              className={
                watch('campaignType') === CampaignType.couponsCampaign
                  ? ''
                  : 'category-select-title-top'
              }
              selectorTypeFieldName={'generalType'}
            />
          ),
        },
        {
          key: "exclude_blacklist_segment_switch_key",
          complexComponent: ({ formContext: { watch } }) => (
            <ReactHookFormSwitch
              customTitleClass={
                watch("isExclusive") || watch('campaignType') === CampaignType.couponsCampaign
                  ? "" : "create-section-label-no-top-space"
              }
              valueFieldName={"excludeBlacklistSegment"}
              title={'Exclude blacklist segment'}
              tips={
                `If the toggle is turned on, will exclude customer in the blacklist segment.${
                watch("isExclusive")
                  ? "\nWhen both Exclude blacklist segment and Exclusive are enabled, customers in the blacklist segment will always be excluded."
                  : ""
                }`}
            />
          )
        },
        {
          key: "blacklist_segment_selector_key",
          component: ReactHookFormMultipleSelector,
          componentProps: {
            valueFieldName: "blacklistSegments",
            loadSourceDataAction: (dispatch) => {
              dispatch(createAction('segments/getAllBlacklistSegmentList')({}));
            },
            getSourceDataFromState: (state) => state.segments.allBlackListSegmentList,
            title: "Blacklist segment",
            custom: {
              customItem: (item) => `[ID: ${item.pk}] ${item.name}`,
            },
            sortable: false
          },
        },
        {
          key: 'campaign_category_selector_key',
          component: CategorySelectorWithPrompt,
        },
        {
          key: 'required_points_input_key',
          component: InputFieldControl,
          componentProps: {
            name: 'requiredPoints',
            title: 'Required points',
            useDefaultFieldName: true,
            type: 'number',
            unit: 'points',
            className: 'coupon-campaign-section-input',
          },
        },
        {
          key: 'ticket_campaign_required_points_key',
          complexComponent: () => (
            <>
              <CustomTitleLabel title="Required points" />
              <label className="section-short-description">
                {'Support ticket without required points only'}
              </label>
            </>
          ),
        },
        {
          key: 'enable_otp_key',
          component: ReactHookFormSwitch,
          componentProps: {
            title: 'Enable OTP',
            valueFieldName: 'enableOtp',
            tips: 'If toggle off, the ticket acquiring flow could be faster but may have occur abuse or fraud cases (End customer may input an unknown email / mobile number).',
          },
        },
      ],
    },
    {
      sectionTitle: 'Set time Slot by linking the coupon (ticket type)',
      getDisplaySectionDescription: ({ formContext: { watch } }) => {
        const ticketCampaignTimeSlots = watch('ticketCampaignTimeSlots');
        let totalAcquiredCoupon = 0;
        let totalUsedCoupon = 0;
        let totalCouponStock = 0;
        ticketCampaignTimeSlots?.length > 0 &&
          ticketCampaignTimeSlots.forEach(({ couponSet }) => {
            if (couponSet?.totalNumberOfAcquiredCoupons) {
              totalAcquiredCoupon += couponSet.totalNumberOfAcquiredCoupons;
            }
            if (couponSet?.totalNumberOfUsedCoupons) {
              totalUsedCoupon += couponSet.totalNumberOfUsedCoupons;
            }
            if (couponSet?.totalNumberOfGeneratedCoupons) {
              totalCouponStock += couponSet.totalNumberOfGeneratedCoupons;
            }
          });
        return (
          `Total number of coupon (ticket type) from linked coupon set:\n` +
          `Acquired/Used/Total Stock: ${totalAcquiredCoupon}/${totalUsedCoupon}/${totalCouponStock}`
        );
      },
      descriptionClass: 'white-space-pre',
      sectionContent: [
        {
          key: 'set_linked_coupon_time_slot_key',
          component: LinkedCouponTimeSlotSection,
        },
      ],
    },
    {
      sectionTitle: 'OTHER INFORMATION',
      sectionContent: [
        {
          key: 'fill_other_info_key',
          component: ReactHookFormSwitch,
          componentProps: {
            title: 'Fill other information',
            valueFieldName: 'fillOtherInfomation',
          },
        },
        {
          key: 'other_info_input_section_key',
          component: OtherInfomationiInput,
        },
      ],
    },
    {
      sectionContent: [
        {
          key: 'visible_mall_key',
          component: VisibleMallSection,
        },
      ],
    },
    {
      sectionTitle: 'Recurring Campaign',
      sectionContent: [
        {
          key: 'recurring_birthday_selector_key',
          complexComponent: ({
            formContext: {
              watch,
              setValue,
              formState: { errors },
            },
          }) => {
            const isBirthdayRecurringCampaign = watch('isBirthdayRecurringCampaign');
            return (
              <RecurringCampaignSwitch
                setValue={(isChecked) => {
                  setValue("isBirthdayRecurringCampaign", isChecked, { shouldDirty: true })
                }}
                defaultValue={isBirthdayRecurringCampaign}
              />
            );
          },
        },
      ],
    },
    {
      sectionTitle: 'Recurring active period',
      sectionContent: [
        {
          key: 'recurring_birthday_active_period_selector_key',
          component: RecurringCampaignPeriodSelector,
          componentProps: {
            prefix: "recurringDate.active"
          }
        },
      ],
    },
    {
      sectionTitle: 'Recurring visible period',
      sectionContent: [
        {
          key: 'recurring_birthday_visible_period_selector_key',
          component: RecurringCampaignPeriodSelector,
          componentProps: {
            prefix: "recurringDate.visible"
          }
        },
      ],
    },
    {
      sectionTitle: 'Active Period',
      getDisplaySectionDescription: ({ formContext: { watch } }) => {
        const linkedEarningRules = watch('linkedEarningRules');
        return campaignAvailableClue(linkedEarningRules?.type);
      },
      sectionContent: [
        {
          key: 'active_period_key',
          complexComponent: ({
            formContext: {
              watch,
              setValue,
              formState: { errors },
            },
          }) => {
            const linkedEarningRules = watch('linkedEarningRules');
            const activeStartDate = watch('activeStartDate');
            const activeEndDate = watch('activeEndDate');
            const earningRuleType = linkedEarningRules?.type;
            const birthdayStartDate =
              linkedEarningRules?.birthdayTypePeriodStartDate;
            const birthdayEndDate =
              linkedEarningRules?.birthdayTypePeriodEndDate;
            return (
              <PeriodSection
                disabled={disableSwitchToAllPeriod(
                  earningRuleType,
                  birthdayStartDate,
                  birthdayEndDate,
                )}
                startErrorObject={{
                  id: 'activeStartDate',
                  error: hasError(errors, 'activeStartDate'),
                  message: errorMessage(errors, 'activeStartDate'),
                }}
                endErrorObject={{
                  id: 'activeEndDate',
                  error: hasError(errors, 'activeEndDate'),
                  message: errorMessage(errors, 'activeEndDate'),
                }}
                endDateChange={(date) => {
                  setValue('activeEndDate', date, { shouldDirty: true });
                }}
                maxDate={campaignLimitDate(earningRuleType, birthdayStartDate)}
                minDate={campaignLimitDate(earningRuleType, birthdayEndDate)}
                switchButtonTitle="It is an all-time active campaign"
                isAlwaysPeriod={watch('isAlwaysActivePeriod')}
                switchOnChange={(value) => {
                  setValue('isAlwaysActivePeriod', value, {
                    shouldDirty: true,
                  });
                }}
                startDate={compareDate(
                  activeStartDate,
                  birthdayStartDate,
                  false,
                )}
                startDateChange={(date) => {
                  setValue('activeStartDate', date, { shouldDirty: true });
                }}
                endDate={compareDate(activeEndDate, birthdayEndDate, true)}
              />
            );
          },
        },
        {
          key: 'blackout_period_key',
          complexComponent: ({ formContext: { watch, setValue } }) => (
            <PeriodAndWeekdaySection
              switchButtonTitle="Blackout period"
              periodValuesField="activePeriodValues"
              periodValues={watch('activePeriodValues')}
              periodValuesChange={(data) => {
                setValue('activePeriodValues', data, { shouldDirty: true });
              }}
              weekday={watch('activeWeekday')}
              weekdayChange={(data) => {
                setValue('activeWeekday', data, { shouldDirty: true });
              }}
              maxDate={watch('activeEndDate')}
              minDate={watch('activeStartDate')}
              disabledDate={!watch('isAlwaysActivePeriod')}
            />
          ),
        },
      ],
    },
    {
      sectionContent: [
        {
          key: 'visible_period_section_key',
          component: VisiblePeriodSection,
        },
      ],
    },
    {
      sectionTitle: 'Coupon acquiring limit (optional)',
      sectionContent: [
        {
          key: 'coupon_acquiring_limit_key',
          component: ReactHookFormSwitch,
          componentProps: {
            title: 'Display the overall limit in the campaign',
            valueFieldName: 'displayOverLimit',
          },
        },
        {
          key: 'per_head_limit_key',
          component: InputFieldControl,
          componentProps: {
            name: 'perHeadLimit',
            title:
              'Per head coupon acquiring limit during the campaign active period',
            useDefaultFieldName: true,
            type: 'number',
            unit: 'number of coupons',
            className: 'coupon-campaign-section-input',
          },
        },
      ],
    },
    {
      sectionTitle: 'MESSAGE FOR RECURRING CAMPAIGN (optional)',
      sectionContent: [
        {
          key: 'recurring_message_selector_key',
          component: RecurringMessages,
        },
      ],
    },
  ],
};

export const CREATE_STEP_FOUR_CONFIG = {
  extraValidate: ({ getValues, setError, clearErrors }) => true,
  getDisplayComponentKeys: ({ formContext: { watch } }) => {
    return {
      displayNormalSectionComponents: [],
      displayLanguageSectionComponents: [
        'language_content_section_key',
        'campaign_profile_section_key',
        ...(watch('campaignType') !== CampaignType.generalMessageCampaign
          ? ['checklist_key']
          : []),
      ],
    };
  },
  languageSections: [
    {
      sectionContent: [
        {
          key: 'language_content_section_key',
          complexComponent: ({ formContext: { getValues }, language }) => (
            <ContentLanguageCard
              language={language}
              campaignData={getValues()}
            />
          ),
        },
      ],
    },
    {
      sectionContent: [
        {
          key: 'campaign_profile_section_key',
          component: ProfileSection,
        },
      ],
    },
    {
      sectionContent: [
        {
          key: 'checklist_key',
          component: ApprovalCheckList,
        },
      ],
    },
  ],
  normalSections: [],
};